import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CookieBanner } from "@/components/consent/CookieBanner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocalModeProvider } from "@/context/LocalModeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Pricing from "@/pages/public/Pricing";
import Legal from "@/pages/public/Legal";
import Directory from "@/pages/public/Directory";
import PublicProfile from "@/pages/public/PublicProfile";
import PublicOrg from "@/pages/public/PublicOrg";
import Events from "@/pages/public/Events";
import PublicEvent from "@/pages/public/PublicEvent";
import Resources from "@/pages/public/Resources";
import SearchResults from "@/pages/public/SearchResults";
import SearchCategory from "@/pages/public/SearchCategory";
import PublicResource from "@/pages/public/PublicResource";
import FounderSuccess from "@/pages/public/FounderSuccess";
import DonationSuccess from "@/pages/public/DonationSuccess";
import DonationManage from "@/pages/public/DonationManage";
import Partner from "@/pages/public/Partner";
import Enterprise from "@/pages/public/Enterprise";
import HandleResolver from "@/pages/public/HandleResolver";
import FeaturesIndex from "@/pages/public/features/FeaturesIndex";
import PersonalProfilesFeature from "@/pages/public/features/PersonalProfilesFeature";
import BusinessProfilesFeature from "@/pages/public/features/BusinessProfilesFeature";
import CapitalProfilesFeature from "@/pages/public/features/CapitalProfilesFeature";
import EventsFeature from "@/pages/public/features/EventsFeature";
import ResourcesFeature from "@/pages/public/features/ResourcesFeature";
import OtterAssistFeature from "@/pages/public/features/OtterAssistFeature";
import AnalyticsFeature from "@/pages/public/features/AnalyticsFeature";
import GroupsFeature from "@/pages/public/features/GroupsFeature";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ResetPassword from "@/pages/auth/ResetPassword";
import SetPassword from "@/pages/auth/SetPassword";
import Welcome from "@/pages/auth/Welcome";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AuthCallback from "@/pages/auth/AuthCallback";
import EmailAction from "@/pages/auth/EmailAction";

import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import AccountSettings from "@/pages/app/AccountSettings";
import AnalyticsPortal from "@/pages/app/analytics/AnalyticsPortal";
import Invitations from "@/pages/app/Invitations";
import MyProfile from "@/pages/app/MyProfile";
import Noted from "@/pages/app/Noted";
import MyOrgs from "@/pages/app/MyOrgs";
import OrgEditor from "@/components/profile/OrgEditor";
import Network from "@/pages/app/Network";
import Contacts from "@/pages/app/contacts/Contacts";
import Groups from "@/pages/app/groups/Groups";
import MyGroups from "@/pages/app/groups/MyGroups";
import GroupPage from "@/pages/app/groups/GroupPage";
import GroupManage from "@/pages/app/groups/GroupManage";
import GroupInviteRedeem from "@/pages/app/groups/GroupInviteRedeem";
import ContactDetail from "@/pages/app/contacts/ContactDetail";
import ContactCard from "@/pages/app/contacts/ContactCard";
import ContactImport from "@/pages/app/contacts/ContactImport";
import SharedCard from "@/pages/app/contacts/SharedCard";
import Duplicates from "@/pages/app/contacts/Duplicates";
import Messages from "@/pages/app/Messages";
import StaffInbox from "@/pages/app/StaffInbox";
import Questions from "@/pages/app/Questions";
import Inquiries from "@/pages/app/Inquiries";
import OtterAssist from "@/pages/app/OtterAssist";
import OtterAssistNeed from "@/pages/app/OtterAssistNeed";
import ChatterPost from "@/pages/app/ChatterPost";
import Bookmarks from "@/pages/app/Bookmarks";
import MyEvents from "@/pages/app/MyEvents";
import EventEditor from "@/pages/app/EventEditor";
import MyResources from "@/pages/app/MyResources";
import ResourceEditor from "@/pages/app/ResourceEditor";
import ActivityFeed from "@/pages/app/ActivityFeed";
import Chatter from "@/pages/app/Chatter";
import Marketing from "@/pages/app/Marketing";
import Plans from "@/pages/app/Plans";
import Billing from "@/pages/app/Billing";
import AdminHome from "@/pages/app/admin/AdminHome";
import AdminOverview from "@/pages/app/admin/AdminOverview";
import AdminAnalytics from "@/pages/app/admin/AdminAnalytics";
import AdminUsers from "@/pages/app/admin/AdminUsers";
import AdminReference from "@/pages/app/admin/AdminReference";
import AdminHandles from "@/pages/app/admin/AdminHandles";
import AdminEntitlements from "@/pages/app/admin/AdminEntitlements";
import AdminPricing from "@/pages/app/admin/AdminPricing";
import AdminLicenses from "@/pages/app/admin/AdminLicenses";
import AdminEmailTemplates from "@/pages/app/admin/AdminEmailTemplates";
import AdminUserDetail from "@/pages/app/admin/AdminUserDetail";
import AdminContent from "@/pages/app/admin/AdminContent";
import AdminStorage from "@/pages/app/admin/AdminStorage";
import AdminAudit from "@/pages/app/admin/AdminAudit";
import AdminSecurityEvents from "@/pages/app/admin/AdminSecurityEvents";
import AdminConsentLog from "@/pages/app/admin/AdminConsentLog";
import OtterAssistAdmin from "@/pages/app/admin/OtterAssistAdmin";
import OtterAssistAdminAssist from "@/pages/app/admin/OtterAssistAdminAssist";
import AdminModeration from "@/pages/app/admin/AdminModeration";
import AdminGroups from "@/pages/app/admin/AdminGroups";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { ChatDockProvider } from "@/context/ChatDockContext";
import { ChatDock } from "@/components/social/ChatDock";
import { CharlaIdentifier } from "@/components/integrations/CharlaIdentifier";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { Button } from "@/components/ui/button";

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <div className="text-6xl font-[700] text-primary">404</div>
      <h1 className="mt-4 text-2xl font-[680] text-foreground">Page not found</h1>
      <p className="mt-2 text-muted-foreground">The page you&apos;re looking for doesn&apos;t exist.</p>
      <Link to="/" className="mt-6"><Button>Back to home</Button></Link>
    </div>
  );
}

function RootLanding() {
  const { user, loading } = useAuth();
  // A returning, fully onboarded member lands on Chatter. Anonymous visitors (and anyone
  // still mid-onboarding, who belongs on /verify-email, /set-password or /welcome) keep
  // seeing the marketing page, which also stays reachable at /home.
  const ready = user?.email_verified && user?.has_password !== false && user?.account_complete;
  if (!loading && ready) return <Navigate to="/chatter" replace />;
  return <Home />;
}


function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback (session_id in URL fragment) before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public marketing + directories */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<RootLanding />} />
        <Route path="/home" element={<Home />} />
        <Route path="/chatter" element={<ProtectedRoute><Chatter /></ProtectedRoute>} />
        <Route path="/chatter/post/:postId" element={<ProtectedRoute><ChatterPost /></ProtectedRoute>} />
        {/* Groups is a PRIMARY Startup Otter destination, not a Dashboard feature. Three
            surfaces, three jobs: /groups DISCOVERS groups (the ONE canonical directory,
            primary nav + normal site shell, GRP-DEC-027), /dashboard/groups MANAGES the
            member's own groups (Dashboard shell: My groups / Pending / Drafts) and
            /g/{handle} is the Group itself. A token only ever redeems at
            /groups/invite/{token} (GRP-DEC-283). Group content is member-only, so an
            anonymous visitor signs in first. */}
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/invite/:token" element={<ProtectedRoute><GroupInviteRedeem /></ProtectedRoute>} />
        <Route path="/g/:handle" element={<GroupPage />} />
        <Route path="/people" element={<Directory kind="people" />} />
        <Route path="/businesses" element={<Directory kind="businesses" />} />
        <Route path="/capital" element={<Directory kind="capital" />} />
        <Route path="/events" element={<Events />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/search/:category" element={<SearchCategory />} />
        <Route path="/features" element={<FeaturesIndex />} />
        <Route path="/features/personal-profiles" element={<PersonalProfilesFeature />} />
        <Route path="/features/business-profiles" element={<BusinessProfilesFeature />} />
        <Route path="/features/capital-profiles" element={<CapitalProfilesFeature />} />
        <Route path="/features/events" element={<EventsFeature />} />
        <Route path="/features/resources" element={<ResourcesFeature />} />
        <Route path="/features/otter-assist" element={<OtterAssistFeature />} />
        <Route path="/features/analytics" element={<AnalyticsFeature />} />
        <Route path="/features/groups" element={<GroupsFeature />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/enterprise" element={<Enterprise />} />
        <Route path="/founders/success" element={<FounderSuccess />} />
        <Route path="/donate/success" element={<DonationSuccess />} />
        <Route path="/donate/manage/:token" element={<DonationManage />} />
        <Route path="/terms" element={<Legal docKey="terms" />} />
        <Route path="/privacy" element={<Legal docKey="privacy" />} />
        <Route path="/returns" element={<Legal docKey="returns" />} />
        <Route path="/accessibility" element={<Legal docKey="accessibility" />} />
        <Route path="/affiliate" element={<Legal docKey="affiliate" />} />
        {/* Public profile/resource/event routes — gated until built in later phases */}
        <Route path="/p/:slug" element={<PublicProfile />} />
        <Route path="/b/:slug" element={<PublicOrg kind="business" />} />
        <Route path="/capital/:slug" element={<PublicOrg kind="capital" />} />
        <Route path="/resource/:slug" element={<PublicResource />} />
        <Route path="/event/:slug" element={<PublicEvent />} />
        {/* Universal @handle route — resolves to the canonical profile URL. Keep LAST so
            it only catches unmatched single segments (static routes rank higher). */}
        <Route path="/:athandle" element={<HandleResolver />} />
      </Route>

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<ProtectedRoute><SetPassword /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/verify-email" element={<ProtectedRoute><VerifyEmail /></ProtectedRoute>} />
      <Route path="/email-change/approve" element={<EmailAction action="approve" />} />
      <Route path="/email-change/verify" element={<EmailAction action="verify" />} />
      <Route path="/email-change/cancel" element={<EmailAction action="cancel" />} />
      <Route path="/recovery-email/verify" element={<EmailAction action="recovery-verify" />} />
      <Route path="/email-recovery/verify" element={<EmailAction action="rec-new-verify" />} />
      <Route path="/email-recovery/confirm" element={<EmailAction action="rec-confirm" />} />
      <Route path="/email-recovery/cancel" element={<EmailAction action="rec-cancel" />} />

      {/* Authenticated app */}
      <Route path="/dashboard" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="plans" element={<Plans />} />
        <Route path="billing" element={<Billing />} />
        <Route path="account" element={<AccountSettings />} />
        <Route path="invitations" element={<Invitations />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="noted" element={<Noted />} />
        <Route path="network" element={<Network />} />
        {/* Relationship CRM Phase 2. Both routes render a "not found" state when the
            server-side config says the CRM isn't available, and every API call behind them
            is owner-scoped + 404s for anyone else. */}
        <Route path="contacts" element={<Contacts />} />
        {/* Groups Batch B — PERSONAL management only (My groups / Pending / Drafts) and
            the per-Group management panel. Public discovery lives solely at /groups, so
            there is exactly one Groups directory. */}
        <Route path="groups" element={<MyGroups />} />
        <Route path="groups/:groupId/manage" element={<GroupManage />} />
        <Route path="contacts/my-card" element={<ContactCard />} />
        <Route path="contacts/import" element={<ContactImport />} />
        <Route path="contacts/duplicates" element={<Duplicates />} />
        <Route path="contacts/card/:token" element={<SharedCard />} />
        <Route path="contacts/:contactId" element={<ContactDetail />} />
        <Route path="messages" element={<Messages />} />
        {/* Staff Messaging V1 — the shared official inbox. The API denies access
            independently of this route (capability + STAFF_MESSAGING_ENABLED). */}
        <Route path="staff-inbox" element={<StaffInbox />} />
        <Route path="questions" element={<Questions />} />
        <Route path="inquiries" element={<Inquiries />} />
        <Route path="otter-assist" element={<OtterAssist />} />
        <Route path="otter-assist/need/:assistId" element={<OtterAssistNeed />} />
        <Route path="bookmarks" element={<Bookmarks />} />
        <Route path="businesses" element={<MyOrgs kind="business" />} />
        <Route path="businesses/:id" element={<OrgEditor kind="business" />} />
        <Route path="capital-profiles" element={<MyOrgs kind="capital" />} />
        <Route path="capital-profiles/:id" element={<OrgEditor kind="capital" />} />
        <Route path="events" element={<MyEvents />} />
        <Route path="events/:id" element={<EventEditor />} />
        <Route path="resources" element={<MyResources />} />
        <Route path="resources/:id" element={<ResourceEditor />} />
        {/* Analytics Portal V1 §12.1: one route, gated server-side. While the portal is
            disabled the gate renders the existing legacy analytics page unchanged. */}
        <Route path="analytics/*" element={<AnalyticsPortal />} />
        <Route path="activity" element={<ActivityFeed />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="admin" element={<AdminHome />}>
          <Route index element={<AdminOverview />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:uid" element={<AdminUserDetail />} />
          <Route path="reference" element={<AdminReference />} />
          <Route path="handles" element={<AdminHandles />} />
          <Route path="entitlements" element={<AdminEntitlements />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="licenses" element={<AdminLicenses />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="storage" element={<AdminStorage />} />
          <Route path="moderation" element={<AdminModeration />} />
          <Route path="groups" element={<AdminGroups />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="security" element={<AdminSecurityEvents />} />
          <Route path="consent-log" element={<AdminConsentLog />} />
          <Route path="otter-assist" element={<OtterAssistAdmin />} />
          <Route path="otter-assist/assist/:assistId" element={<OtterAssistAdminAssist />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LocalModeProvider>
            <RealtimeProvider>
              <ChatDockProvider>
                <ScrollToTop />
                <AppRouter />
                <ChatDock />
                <CharlaIdentifier />
                <CookieBanner />
                <Toaster position="top-right" richColors closeButton />
              </ChatDockProvider>
            </RealtimeProvider>
          </LocalModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
